// Tìm đường dẫn tuyệt đối của binary `avatar` để RE-EXEC sau `avatar update` (#211 phase-03).
//
// Vì sao KHÔNG dùng `process.argv[1]` (khác hẳn `spawn-sync-in-workspace.ts:52`, nơi cố ý
// dùng nó): ở đây `npm i -g` VỪA thay file trên đĩa, còn tiến trình đang chạy vẫn là bản CŨ.
// `argv[1]` trỏ entry của bản cũ ⇒ re-exec bằng nó = sync bằng đúng code người dùng vừa
// yêu cầu thay. Phải resolve LẠI theo TÊN lệnh trên PATH.
//
// Port 2 tầng từ extension (`src/extension/src/lib/resolve-binary-path-from-env.ts:104`),
// BỎ tầng override: setting `avatar.binaryPath` là của extension, CLI không có khái niệm đó.
import { accessSync, constants, statSync } from "node:fs";
import { delimiter, join } from "node:path";

/** Đuôi file thực thi trên Windows; POSIX không cần đuôi. */
const WINDOWS_EXECUTABLE_SUFFIXES: readonly string[] = [".cmd", ".exe", ".bat", ""];
const POSIX_EXECUTABLE_SUFFIXES: readonly string[] = [""];

/** Quét danh sách thư mục cho trước, trả ứng viên CHẠY ĐƯỢC đầu tiên. */
function findInDirectories(
  binaryName: string,
  directories: readonly string[],
  platform: NodeJS.Platform,
): string | null {
  const suffixes = platform === "win32" ? WINDOWS_EXECUTABLE_SUFFIXES : POSIX_EXECUTABLE_SUFFIXES;

  for (const directory of directories) {
    if (directory.length === 0) continue;
    for (const suffix of suffixes) {
      const candidate = join(directory, `${binaryName}${suffix}`);
      try {
        if (statSync(candidate).isFile()) {
          accessSync(candidate, constants.X_OK);
          return candidate;
        }
      } catch {
        // Không tồn tại hoặc không có quyền chạy — thử ứng viên kế tiếp.
      }
    }
  }
  return null;
}

/** Tầng 1: quét PATH. `null` = PATH rỗng hoặc không có ứng viên nào chạy được. */
export function resolveBinaryPathFromEnv(
  binaryName: string,
  pathEnv: string | undefined = process.env.PATH,
  platform: NodeJS.Platform = process.platform,
): string | null {
  if (pathEnv === undefined || pathEnv.length === 0) return null;
  return findInDirectories(binaryName, pathEnv.split(delimiter), platform);
}

/**
 * Tầng 2: nơi npm hay đặt binary toàn cục, dùng khi PATH KHÔNG có.
 *
 * Vì sao cần dù CLI chạy trong terminal (đã có PATH của shell): npm prefix riêng
 * (nvm/volta/`~/.npm-global`) đặt binary ra ngoài PATH mặc định — và bản `avatar` vừa cài
 * nằm đúng ở đó. Mất tầng này ⇒ `update` báo "không tìm thấy binary" đúng lúc cần nó nhất.
 *
 * Windows trả rỗng: PATH tiến trình kế thừa từ registry nên đã có `%APPDATA%\npm`; đoán
 * bừa đường dẫn chưa từng đo thì tệ hơn không đoán.
 */
export function fallbackBinaryDirectories(
  home: string | undefined = process.env.HOME,
  platform: NodeJS.Platform = process.platform,
): string[] {
  if (platform === "win32") return [];
  const directories = ["/opt/homebrew/bin", "/usr/local/bin"];
  if (home !== undefined && home.length > 0) {
    directories.push(
      join(home, ".npm-global", "bin"),
      join(home, ".volta", "bin"),
      join(home, ".local", "bin"),
    );
  }
  return directories;
}

export interface ResolveAvatarBinaryOptions {
  pathEnv?: string | undefined;
  home?: string | undefined;
  platform?: NodeJS.Platform;
  /**
   * Tiêm danh sách thư mục dự phòng. Chỉ test dùng: mặc định quét cả `/opt/homebrew/bin`,
   * mà trên máy dev thư mục đó có `avatar` THẬT ⇒ test tự dựng binary giả sẽ đo nhầm máy
   * thay vì đo hàm.
   */
  fallbackDirectories?: readonly string[];
}

/**
 * Hai tầng theo thứ tự: PATH → thư mục npm global hay gặp.
 * `null` ⇒ call site phải hiện HƯỚNG DẪN CÀI, KHÔNG im lặng bỏ qua bước sync.
 */
export function resolveAvatarBinaryPath(
  binaryName: string,
  options: ResolveAvatarBinaryOptions = {},
): string | null {
  const platform = options.platform ?? process.platform;

  const fromPath = resolveBinaryPathFromEnv(
    binaryName,
    options.pathEnv ?? process.env.PATH,
    platform,
  );
  if (fromPath !== null) return fromPath;

  return findInDirectories(
    binaryName,
    options.fallbackDirectories ??
      fallbackBinaryDirectories(options.home ?? process.env.HOME, platform),
    platform,
  );
}
